/* eslint-disable css-modules/no-unused-class */
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
// jl-design-system
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';
import FormFieldErrorMessage from 'jl-design-system/components/form-field-error-message/FormFieldErrorMessage';
import Form from 'jl-design-system/components/form/Form';
import { resetForm, submitForm, clearFormSubmitErrors } from 'jl-design-system/redux/actions/form/formActions';
// redux
import { connect } from 'react-redux';
import { go } from 'connected-react-router';
import { triggerAnalyticsEvent } from '../../redux/actions/analytics/analyticsAction';
// config
import { GIFT_CARD_NUMBER } from '../../utils/form/configs/giftFields';
import { changeGiftOption, redirectFromGiftCardToPayment } from '../../redux/actions/payment/paymentActions';
// components
import {
  validateGiftOptionsInputDataLength,
  getErrorMessage,
  getHint,
  suggestPrepaidCard,
} from '../../redux/reducers/payment/giftOptionsHigherOrderReducer';
import PROGRESS_STATE from '../../constants/progressStateConstants';
import { GIFT_OPTIONS } from '../../constants/giftOptionsConstants';
import { ANALYTICS_PREPAID_GIFTCARD_MESSAGE, ANALYTICS_GIFT_OPTIONS_CHANGE_TO_PREPAID } from '../../constants/actionConstants';
import Container from '../container';
// styles
import styles from './apply-gift-options-forms.scss';


export class GiftOptionsForm extends PureComponent {
  static propTypes = {
    changeGiftOption: PropTypes.func,
    clearFormSubmitErrors: PropTypes.func,
    config: PropTypes.shape({
      fields: PropTypes.arrayOf(PropTypes.object),
      id: PropTypes.string,
    }),
    dataTest: PropTypes.string,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.string,
    errorMessageId: PropTypes.string,
    formContainerClassName: PropTypes.string,
    formHasSyncErrors: PropTypes.bool,
    formSubmitFailed: PropTypes.bool,
    fullDataEntered: PropTypes.bool,
    go: PropTypes.func,
    isLoading: PropTypes.bool,
    onFormSubmit: PropTypes.func,
    redirectFromGiftCardToPayment: PropTypes.func,
    resetForm: PropTypes.func,
    submitForm: PropTypes.func,
    triggerAnalyticsEvent: PropTypes.func,
    values: PropTypes.shape({
      giftCardNumber: PropTypes.string,
    }),
  };

  static defaultProps = {
    changeGiftOption: window.defaultFunc,
    clearFormSubmitErrors: window.defaultFunc,
    config: { fields: [] },
    dataTest: '',
    disabled: false,
    errorMessage: '',
    errorMessageId: '',
    formHasSyncErrors: false,
    formSubmitFailed: false,
    go: window.defaultFunc,
    isLoading: false,
    onFormSubmit: window.defaultFunc,
    redirectFromGiftCardToPayment: window.defaultFunc,
    resetForm: window.defaultFunc,
    submitForm: window.defaultFunc,
    triggerAnalyticsEvent: window.defaultFunc,
    values: {},
  };

  static getDerivedStateFromProps(props, state) {
    const { hint, oldPropsIsLoading } = state;

    const { giftVoucherSecurityCode, giftVoucherSerialNumber } = props.values || {};

    const newHint = getHint({ giftVoucherSecurityCode, giftVoucherSerialNumber });

    // loading starts
    if (!oldPropsIsLoading && props.isLoading) {
      return {
        hint: newHint,
        oldPropsIsLoading: props.isLoading,
        progress: PROGRESS_STATE.LOADING,
      };
    }

    // loading ends
    if (oldPropsIsLoading && !props.isLoading) {
      if (props.formSubmitFailed) {
        return {
          hint: newHint,
          oldPropsIsLoading: props.isLoading,
          progress: PROGRESS_STATE.FAILED,
        };
      }
      return {
        hint: newHint,
        oldPropsIsLoading: props.isLoading,
        progress: PROGRESS_STATE.SUCCESS,
      };
    }

    // prevent getDerivedStateFromProps from overwriting gift voucher hint unless it changes
    if (newHint !== hint && hint !== 'gift voucher') {
      return {
        hint: newHint,
        oldPropsIsLoading: props.isLoading,
      };
    }

    return {
      oldPropsIsLoading: props.isLoading,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      progress: this.props.isLoading
        ? PROGRESS_STATE.LOADING
        : PROGRESS_STATE.NONE,
      hint: '',
      oldPropsIsLoading: undefined,
      prepaidCardLinkText: '',
    };
  }

  componentDidUpdate(prevProps) {
    const {
      config,
      isLoading,
      fullDataEntered,
      submitForm,
      clearFormSubmitErrors,
      formHasSyncErrors,
      formSubmitFailed,
      values,
    } = this.props;

    // form filled with valid data or new data that was pasted in full
    const formErrorsRemoved = prevProps.formHasSyncErrors && !formHasSyncErrors;
    const formValuesChanged = prevProps.values !== values;

    if (fullDataEntered && (formErrorsRemoved || formValuesChanged)) {
      setTimeout(() => {
        submitForm(config.id);
      }, 0);
    }

    // form completes successful submit
    if ((prevProps.isLoading && !isLoading) && !formSubmitFailed) {
      this.resetForm();
    }

    // form gets edited after unsuccessful submit
    if (formSubmitFailed && (prevProps.fullDataEntered && !fullDataEntered)) {
      clearFormSubmitErrors(config.id);
    }

    // form submit ends
    if (prevProps.isLoading && !isLoading) {
      this.resetProgress();
    }
  }

  resetForm() {
    const {
      config: { id },
      resetForm,
    } = this.props;

    resetForm(id);
  }

  resetProgress() {
    this.setState({
      progress: PROGRESS_STATE.NONE,
    });
  }

  onGiftCardNumberKeyUp = (evt) => {
    // gift card hint
    const { values, triggerAnalyticsEvent } = this.props;
    const hint = getHint({ giftCardNumberKey: evt.key, giftCardNumberValue: values.giftCardNumber });
    const prepaidCardLinkText = suggestPrepaidCard(values.giftCardNumber);
    if (prepaidCardLinkText) {
      triggerAnalyticsEvent(ANALYTICS_PREPAID_GIFTCARD_MESSAGE);
    }
    this.setState({
      hint,
      prepaidCardLinkText,
    });
  };

  closeModal = () => {
    const {
      go,
    } = this.props;

    go(-1);
  };

  changeGiftOption = () => {
    const { changeGiftOption } = this.props;
    const { hint } = this.state;

    const changeTo = hint === 'gift card' ? GIFT_OPTIONS.GIFT_CARD : GIFT_OPTIONS.GIFT_VOUCHER;
    changeGiftOption(changeTo);
  };

  changeToCardPayment = () => {
    const {
      redirectFromGiftCardToPayment,
      triggerAnalyticsEvent,
      values,
    } = this.props;

    this.closeModal();

    triggerAnalyticsEvent(ANALYTICS_GIFT_OPTIONS_CHANGE_TO_PREPAID);
    redirectFromGiftCardToPayment({ cardNumber: values.giftCardNumber });
  };

  render() {
    const {
      onFormSubmit,
      config,
      errorMessage,
      errorMessageId,
      dataTest,
      formContainerClassName,
      disabled,
    } = this.props;

    const { progress, hint, prepaidCardLinkText } = this.state;

    config.fields = config.fields.map(field => ({
      ...field,
      progress,
      ...(field.id === GIFT_CARD_NUMBER.id ? {
        extensionProps: {
          onKeyUp: this.onGiftCardNumberKeyUp,
        },
      } : {}),
    }));

    return (
      <div className={formContainerClassName}>
        <Form
          config={config}
          data-testid={dataTest}
          disabled={disabled}
          form={config.id}
          onSubmit={onFormSubmit}
          progress={progress}
          useFormValidationErrorsAlertMessage={false}
        />
        {
          !hint && !prepaidCardLinkText && errorMessage &&
          <FormFieldErrorMessage
            data-testid="gift-options-form-error"
            id={errorMessageId}
            message={errorMessage}
            roleAlert
          />
        }
        {
          hint && !prepaidCardLinkText &&
          <p className={styles.hint} data-testid={`${dataTest}-hint`}>
            Are you trying to enter a{' '}
            <UnderlinedButton
              onClick={this.changeGiftOption}
            >
              { hint }
            </UnderlinedButton>
            ?
          </p>
        }
        {prepaidCardLinkText && (
          <Container marginBottom="0" marginTop="1" testId={`${dataTest}-prepaid-hint`}>
            This does not look like a valid gift card number. If you are entering a pre-paid card, please enter{' '}
            <UnderlinedButton
              data-testid="prepaid-card-hint-button"
              onClick={this.changeToCardPayment}
            >
              { prepaidCardLinkText }
            </UnderlinedButton>
          </Container>
        )}
      </div>
    );
  }
}

export function mapStateToProps(state, ownProps) {

  const formId = ownProps?.config?.id ?? '';
  const form = state.form?.[formId] ?? {};

  const { values, syncErrors } = form;

  const fullDataEntered = validateGiftOptionsInputDataLength(values);
  const errorMessage = getErrorMessage(formId, form);
  const errorMessageId = `${formId}-error-message`;

  return {
    fullDataEntered,
    formHasSyncErrors: !!syncErrors,
    errorMessage,
    errorMessageId,
    values,
  };
}

export function mapDispatchToProps() {
  return {
    changeGiftOption,
    clearFormSubmitErrors,
    redirectFromGiftCardToPayment,
    go,
    resetForm,
    submitForm,
    triggerAnalyticsEvent,
  };
}

export default connect((state, ownProps) => mapStateToProps(state, ownProps), mapDispatchToProps())(GiftOptionsForm);
